import type { Styles } from '@adornis/ass/style.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/calendar/calendar.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/datetime/x-timerange-picker.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { addOpenTimeSlot, removeOpenTimeSlot } from '@ils/evaluation-scheduler/api/time-slot.js';
import { Names } from '@ils/static/names.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { map } from 'rxjs';
import { subscribeMyCalendarEvents } from '../api/events.js';
import { CalendarEvent } from '../db/calendarEvent.js';
import { TimeSlot } from '../db/time-slot.js';
import { MIN_SLOT_EVALUATION_TIME } from '../minSlotEvaluationTime.js';
import './ils-countdown.js';
import './ils-evaluation-termin-card.js';

@customElement('ils-evaluation-timeallocation')
export class ILSEvaluationTimeAllocation extends ChemistryLitElement {
  @state() private readonly _user = new RXController(this, currentUser());
  override render() {
    return html`
      <x-section space="md" h100 padding="md">
        <x-flex space="xs">
          <x-h4 ${css({ marginBottom: this.spacing.xxs })}> Legende </x-h4>
          ${this.__terminLegendItem(this.colors.accent, 'Deine freien Zeiten')}
          ${this.__terminLegendItem(this.colors.tone.error, 'Deine Termine zur Bewertung')}
        </x-flex>
        ${this._renderCalendar()}
      </x-section>
    `;
  }

  private __terminLegendItem(color: string, title: string) {
    return html`
      <x-flex horizontal crossaxis-center space="sm">
        <x-flex ${css({ width: '15px', aspectRatio: '1 / 1', borderRadius: '50%', background: color })}></x-flex>
        <x-text> ${title} </x-text>
      </x-flex>
    `;
  }

  private _renderCalendar() {
    return html`
      <x-flex padding="sm" ${css({ height: '100%' })}>
        <x-flex horizontal end>
          <x-button icon="add" @click=${() => this._showTimeSlotDialog()}> Freien Zeit-Slot hinzufügen </x-button>
        </x-flex>
        <x-calendar
          ${css({ height: '100%' })}
          week
          selectable
          .options=${{ allDaySlot: false, nowIndicator: true }}
          .eventsProvider=${(start: Date, end: Date) =>
            subscribeMyCalendarEvents(
              start,
              end,
            )(selectionSet(() => CalendarEvent, 2)).pipe(
              map(events =>
                events.map(event => {
                  const displayEvent: any = {
                    start: event.start.toJSDate(),
                    end: event.end.toJSDate(),
                    extendedProps: { reference: event._id },
                  };
                  if (event._class === Names.EvaluationTermin._class) displayEvent.color = this.colors.tone.error;
                  return displayEvent;
                }),
              ),
            )}
          @select=${async (e: CustomEvent) => {
            const start = DateTime.fromJSDate(e.detail.start);
            const end = DateTime.fromJSDate(e.detail.end);

            await this._addTimeSlot(start, end);
          }}
          @eventClick=${async (e: CustomEvent) => {
            if (e.detail.event.backgroundColor === this.colors.tone.error.string) return;

            const start = DateTime.fromJSDate(e.detail.event.start);
            const end = DateTime.fromJSDate(e.detail.event.end);
            if (
              !(await XNativeDialog.confirm(
                'Möchtest du den Zeitslot von ' +
                  start.toLocaleString(DateTime.DATETIME_MED) +
                  ' bis ' +
                  end.toLocaleString(DateTime.DATETIME_MED) +
                  ' wirklich löschen?',
              ))
            )
              return;
            const timeSlot = e.detail.event.extendedProps.reference;
            if (!timeSlot) return;

            try {
              await XNativeDialog.waitFor(removeOpenTimeSlot(timeSlot)());
            } catch (e) {
              void XNativeDialog.alert((e as Error).message);
            }
          }}
        ></x-calendar>
      </x-flex>
    `;
  }

  private async _showTimeSlotDialog() {
    // todo: refactor x-input date and x-timerange-picker to single x-input datetime-local
    try {
      const { start, end } = await XDialog.showPopup<{ start: DateTime; end: DateTime }>({
        content: (resolve, reject) => {
          const data: { date: DateTime; timerange: [DateTime, DateTime] } = {
            date: DateTime.now(),
            timerange: [DateTime.now(), DateTime.now().plus({ hours: 1 })],
          };
          return html`
            <x-flex padding="md" space="md">
              <x-h4> Freien Zeit-Slot auswählen </x-h4>
              <x-input
                type="date"
                placeholder="Datum auswählen..."
                @value-changed=${(e: ValueEvent<DateTime>) => (data.date = e.detail.value)}
              ></x-input>
              <x-timerange-picker
                placeholder="Zeit auswählen..."
                @value-changed=${(e: ValueEvent<[DateTime, DateTime]>) => (data.timerange = e.detail.value)}
              ></x-timerange-picker>
              <x-flex horizontal end space="md">
                <x-button mode="outline" @click=${reject}> Abbrechen </x-button>
                <x-button
                  @click=${() =>
                    resolve({
                      start: data.date.set({ hour: data.timerange[0].hour, minute: data.timerange[0].minute }),
                      end: data.date.set({ hour: data.timerange[1].hour, minute: data.timerange[1].minute }),
                    })}
                >
                  Hinzufügen
                </x-button>
              </x-flex>
            </x-flex>
          `;
        },
      });
      await this._addTimeSlot(start, end);
    } catch {
      /* cancelled */
    }
  }

  private async _addTimeSlot(start: DateTime, end: DateTime) {
    if (start < DateTime.now()) return XNativeDialog.alert('Du kannst keine Zeitslots in der Vergangenheit anlegen');

    if (
      !this._user.value ||
      !(await XNativeDialog.confirm(
        'Möchtest du einen Zeitslot von ' +
          start.toLocaleString(DateTime.DATETIME_MED) +
          ' bis ' +
          end.toLocaleString(DateTime.DATETIME_MED) +
          ' anlegen?',
      ))
    )
      return;

    const interval = start.until(end);
    const minLength = MIN_SLOT_EVALUATION_TIME;
    if (Math.ceil(interval.toDuration('minutes').minutes) <= minLength)
      return XNativeDialog.alert('Du musst mindestens ' + minLength + ' Minuten auswählen');

    const maxLength = 10 * 60; // 10 Stunden
    if (Math.ceil(interval.toDuration('minutes').minutes) > maxLength)
      return XNativeDialog.alert('Du darfst nicht mehr als 10 Stunden am Stück auswählen');

    try {
      // Create the new time slot
      const newSlot = new TimeSlot({
        start,
        end,
        userIDs: [this._user.value._id],
      });

      // Add the slot (server will handle merging) and get the count of merged slots
      const mergedCount = await XNativeDialog.waitFor(addOpenTimeSlot(newSlot)());

      // Show a success message with information about merged slots
      if (mergedCount > 0) {
        await XNativeDialog.alert(
          `Zeitslot wurde erfolgreich hinzugefügt und mit ${mergedCount} bestehenden Slot${
            mergedCount === 1 ? '' : 's'
          } zusammengeführt.`,
        );
      }
    } catch (e) {
      return XNativeDialog.alert((e as Error).message);
    }
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          height: '100%',
        },
      },
    ];
  }
}
